<template>
  <v-container>
    <div class="back-nav">
      <v-btn text :to="{ name: 'employees' }" exact>
        <v-icon left>mdi-chevron-left</v-icon>
        My employees
      </v-btn>
    </div>
    <div class="header">
      <h2>
        <v-icon>$addemployees</v-icon>
        Add Employees
      </h2>
    </div>
    <v-row>
      <v-col cols="12" md="8">
        <pt-sheet>
          <v-card-title>
            <h1>Manually Add Employees</h1>
          </v-card-title>
          <v-card-subtitle class="mt-0 mb-4">
            <h3>We’ll need some basic information about your employees.</h3>
            <h3>
              You can also
              <v-btn text :to="{ name: 'employeesUpload' }" class="inline"
                >bulk upload here.</v-btn
              >
            </h3>
          </v-card-subtitle>
          <v-card-text>
            <v-form v-model="formValid" ref="form">
              <v-container pa-0>
                <v-row no-gutters>
                  <v-col cols="12" sm="6" lg="4">
                    <v-text-field
                      class="mr-sm-4"
                      label="First Name*"
                      v-model="employee.firstName"
                      filled
                      dense
                      :rules="[rules.required('First Name')]"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" lg="4">
                    <v-text-field
                      label="Last Name*"
                      v-model="employee.lastName"
                      filled
                      dense
                      :rules="[rules.required('Last Name')]"
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12" sm="6" lg="4">
                    <v-text-field
                      class="mr-sm-4"
                      label="SSN*"
                      v-model="employee.ssn"
                      v-mask="showSsn ? mask : hiddenMask"
                      masked="false"
                      filled
                      dense
                      :append-icon="showSsn ? 'mdi-eye' : 'mdi-eye-off'"
                      :type="showSsn ? 'text' : 'password'"
                      @click:append="showSsn = !showSsn"
                      :rules="[rules.required('SSN'), rules.nDigits('SSN', 9)]"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" lg="4">
                    <date-field
                      v-model="employee.dob"
                      :maxDate="this.getNow()"
                      label="Date of Birth*"
                      :rules="[rules.required('Date of Birth')]"
                    >
                    </date-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12" lg="6">
                    <v-text-field
                      label="National Provider Identifier (NPI)"
                      v-model="employee.npi"
                      v-mask="npiMask"
                      filled
                      dense
                      hint="NPI is a unique identifier for covered health care providers."
                      persistent-hint
                      :rules="[rules.nDigits('NPI', 10)]"
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-divider class="my-8"></v-divider>
                <v-row no-gutters>
                  <v-col cols="12" lg="6">
                    <v-text-field
                      label="Home Address"
                      v-model="employee.street1"
                      filled
                      dense
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12" lg="6">
                    <v-text-field
                      label="Suite, APT (optional)"
                      v-model="employee.street2"
                      filled
                      dense
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12" sm="8" lg="4">
                    <v-text-field
                      class="mr-sm-4"
                      label="City"
                      v-model="employee.city"
                      filled
                      dense
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4" lg="2">
                    <v-autocomplete
                      label="State"
                      :items="states"
                      v-model="employee.state"
                      item-value="state"
                      item-text="code"
                      filled
                      dense
                      clearable
                    >
                    </v-autocomplete>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12" lg="4">
                    <v-text-field
                      label="Zip Code"
                      v-model="employee.postalCode"
                      filled
                      dense
                      class="mr-lg-4"
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-container class="text-right pa-0 mr-sm-4">
              <v-btn
                class="mb-4 pl-10"
                depressed
                color="primary"
                @click="saveEmployee(false)"
                :block="$vuetify.breakpoint.xsOnly"
                :disabled="saveDisabled"
              >
                Save
                <v-progress-circular
                  class="ml-1"
                  :class="!saving && 'visibility-hidden'"
                  indeterminate
                  size="20"
                ></v-progress-circular>
              </v-btn>
              <v-btn
                class="mb-4 ml-sm-4 pl-10"
                depressed
                color="primary"
                @click="saveEmployee(true)"
                :block="$vuetify.breakpoint.xsOnly"
                :disabled="saveDisabled"
              >
                Save and Add Another
                <v-icon right>mdi-arrow-right-thick</v-icon>
                <v-progress-circular
                  class="ml-1"
                  :class="!saving && 'visibility-hidden'"
                  indeterminate
                  size="20"
                ></v-progress-circular>
              </v-btn>
            </v-container>
          </v-card-actions>
        </pt-sheet>
      </v-col>
      <v-col cols="12" md="4">
        <!--        Intentionally empty-->
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mask } from "vue-the-mask";
import { post } from "@/services/request";
import { employeesAPI } from "@/services/api";
import { datePickerNow } from "@/services/util";
import PtSheet from "@/components/PtSheet.vue";
import DateField from "@/components/DateField";
import hasConstant from "@/mixins/has-constant";
import * as rules from "@/services/rules";
import hasNotifications from "@/mixins/has-notifications";

const emptyEmployee = () => ({
  firstName: null,
  lastName: null,
  dob: null,
  ssn: null,
  npi: null,
  street1: null,
  street2: null,
  city: null,
  state: null,
  postalCode: null,
  country: "US"
});

export default {
  name: "AddEmployee",
  mixins: [hasConstant, hasNotifications],
  components: {
    "pt-sheet": PtSheet,
    "date-field": DateField
  },
  directives: {
    mask
  },
  data() {
    return {
      mask: "###-##-####",
      hiddenMask: "#########",
      npiMask: "##########",
      showSsn: false,
      menu: false,
      formValid: false,
      rules,
      employee: emptyEmployee(),
      saving: false
    };
  },
  computed: {
    saveDisabled() {
      return !this.formValid || this.saving;
    }
  },
  methods: {
    saveEmployee(addAnother) {
      this.saving = true;

      post(employeesAPI, this.employee)
        .then(response => {
          if (response) {
            this.showMessage(
              `You've successfully added employee '` +
                this.employee.firstName +
                ` ` +
                this.employee.lastName +
                `'.`
            );
            if (addAnother) {
              this.$refs.form.reset();
              this.employee = emptyEmployee();
              this.$vuetify.goTo(".back-nav");
            } else this.$router.push("/employees");
          }
        })
        .catch(() =>
          this.showMessage({
            text:
              "Error adding employees, please try again or contact us for support",
            mode: "error"
          })
        )
        .finally(() => {
          this.saving = false;
        });
    },
    getNow: datePickerNow
  }
};
</script>

<style lang="sass" scoped>
h1
  font-size: 40px
  line-height: 50px
  color: $darker-grey

h2
  font-size: 35px
  line-height: 44px
  color: $darker-grey

h3
  font-size: 24px
  font-weight: normal
  line-height: 31px
  color: $darker-grey

.visibility-hidden
    visibility: hidden
</style>
